import { Bug, GraduationCap, Lightbulb, MessageCircleQuestion } from 'lucide-react';
import { DOMAINS, ROLE_LABELS } from '../ui';

/**
 * The words and small rules every support screen shares — categories, who is
 * looking, how a date or a thread entry reads. Status and priority tones live
 * in `ui/statusVocabulary` (`supportTicket`, `ticketPriority`).
 *
 * The enums are the server's (`vc_be/utils/supportTicketRules.js`).
 */

export const ACTIVE_STATUSES = ['open', 'in_progress'];
export const DONE_STATUSES = ['resolved', 'closed'];
export const isActive = (status) => ACTIVE_STATUSES.includes(status);

/**
 * `label` names the category in a table or a detail panel. A topic card asks
 * the question from the requester's side instead, so it has its own `topic`
 * and a `blurb` per audience — a trainer's "institute question" is about
 * batches and payouts, a student's about classes and fees.
 */
export const CATEGORIES = [
  {
    value: 'platform_bug',
    label: 'Technical problem',
    topic: 'Something isn’t working',
    icon: Bug,
    blurb: {
      student: 'A page won’t load, a video won’t play, or something won’t save.',
      instructor: 'A class, recording, upload or page isn’t behaving.',
      staff: 'An error, a broken page or something that won’t save.',
    },
  },
  {
    value: 'institute_issue',
    label: 'Institute question',
    topic: 'A question for the institute',
    icon: GraduationCap,
    blurb: {
      student: 'Your batch, schedule, fees, certificate or course content.',
      instructor: 'Your batches, schedule, students or payouts.',
      staff: 'Setup, data or an account in your institute.',
    },
  },
  {
    value: 'feature_request',
    label: 'Feature request',
    topic: 'Suggest an improvement',
    icon: Lightbulb,
    blurb: {
      student: 'An idea that would make learning here easier.',
      instructor: 'An idea that would make teaching here easier.',
      staff: 'Something you wish the system did.',
    },
  },
  {
    value: 'other',
    label: 'Other',
    topic: 'Something else',
    icon: MessageCircleQuestion,
    blurb: {
      student: 'Anything that doesn’t fit the other three.',
      instructor: 'Anything that doesn’t fit the other three.',
      staff: 'Anything that doesn’t fit the other three.',
    },
  },
];

const CATEGORY_BY_VALUE = Object.fromEntries(CATEGORIES.map((c) => [c.value, c]));
export const categoryOf = (value) => CATEGORY_BY_VALUE[value] || null;
export const categoryLabel = (value) => CATEGORY_BY_VALUE[value]?.label || (value ? String(value) : '—');

/** What each priority means, said to the person choosing it. */
export const PRIORITIES = [
  { value: 'low', label: 'Low', hint: 'A question, or a minor issue with a workaround.' },
  { value: 'medium', label: 'Medium', hint: 'Something isn’t working as it should.' },
  { value: 'high', label: 'High', hint: 'It’s stopping me from getting work done.' },
  { value: 'critical', label: 'Critical', hint: 'Nobody can use it, or data is at risk.' },
];

const labelsOf = (domain) => Object.entries(DOMAINS[domain]).map(([value, { label }]) => ({ value, label }));
export const STATUS_OPTIONS = labelsOf('supportTicket');
export const PRIORITY_OPTIONS = labelsOf('ticketPriority');
export const CATEGORY_OPTIONS = CATEGORIES.map(({ value, label }) => ({ value, label }));

export const statusLabel = (value) => DOMAINS.supportTicket[value]?.label || value || '—';
export const priorityLabel = (value) => DOMAINS.ticketPriority[value]?.label || value || '—';

const STAFF_ROLES = ['superadmin', 'admin', 'tenantadmin'];
export const isStaffRole = (usertype) => STAFF_ROLES.includes(usertype);

/** Whose blurb a topic card shows. */
export const audienceOf = (usertype) => {
  if (usertype === 'instructor') return 'instructor';
  if (isStaffRole(usertype)) return 'staff';
  return 'student';
};

/**
 * The role chip on a thread entry. A requester is not shown our internal
 * titles — "Platform Admin" and "Super Admin" mean nothing to a student, and
 * the first would name the platform on a white-labelled page — so every staff
 * author reads as "Support team" to them.
 */
export const authorRoleLabel = (role, viewerIsStaff) => {
  if (!role) return '';
  if (!viewerIsStaff && isStaffRole(role)) return 'Support team';
  return ROLE_LABELS[role] || role;
};

export const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

const DATE_TIME = { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' };
const DATE_ONLY = { day: 'numeric', month: 'short', year: 'numeric' };

export const formatDateTime = (value) => (value ? new Date(value).toLocaleString('en-IN', DATE_TIME) : '—');
export const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN', DATE_ONLY) : '—');

/** "just now" · "5 min ago" · "3 h ago" · "2 days ago" · then a date. */
export const timeAgo = (value, now = Date.now()) => {
  if (!value) return '';
  const mins = Math.floor((now - new Date(value).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDate(value);
};

/** A span of time, two units at most: "45 min" · "3 h 20 min" · "2 d 4 h". */
export const formatDuration = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(Number(ms))) return '—';
  const mins = Math.max(0, Math.round(Number(ms) / 60000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return mins % 60 ? `${hours} h ${mins % 60} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days} d ${hours % 24} h` : `${days} d`;
};

/** "Chrome 129 on Windows" from a user-agent string; '' when it says nothing usable. */
export const browserLabel = (ua) => {
  if (!ua) return '';
  const browser =
    ua.match(/Edg\/(\d+)/) ? `Edge ${ua.match(/Edg\/(\d+)/)[1]}`
      : ua.match(/OPR\/(\d+)/) ? `Opera ${ua.match(/OPR\/(\d+)/)[1]}`
        : ua.match(/Firefox\/(\d+)/) ? `Firefox ${ua.match(/Firefox\/(\d+)/)[1]}`
          : ua.match(/Chrome\/(\d+)/) ? `Chrome ${ua.match(/Chrome\/(\d+)/)[1]}`
            : ua.match(/Version\/(\d+).*Safari/) ? `Safari ${ua.match(/Version\/(\d+).*Safari/)[1]}`
              : '';
  const os =
    /Windows/.test(ua) ? 'Windows'
      : /Android/.test(ua) ? 'Android'
        : /iPhone|iPad|iPod/.test(ua) ? 'iOS'
          : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
            : /Linux/.test(ua) ? 'Linux'
              : '';
  if (browser && os) return `${browser} on ${os}`;
  return browser || os || ua.slice(0, 60);
};

/** A recorded change, as the sentence after the actor's name. */
export const eventSentence = (entry) => {
  const { field, from, to } = entry?.event || {};
  if (entry?.text === 'reopened this by replying') return 'reopened this by replying';
  if (entry?.text === 'marked this as solved') return 'marked this as solved';
  if (field === 'status') return `changed the status to ${statusLabel(to)}`;
  if (field === 'priority') return `changed the priority to ${priorityLabel(to)}`;
  if (field === 'category') return `moved this to ${categoryLabel(to)}`;
  if (field === 'assignedTo') return to ? `assigned this to ${to}` : `removed the assignee${from ? ` (${from})` : ''}`;
  return entry?.text || 'updated this ticket';
};

/** The context a new ticket carries, read at the moment it is filed. */
export const captureContext = () => ({
  pageUrl: window.location.pathname + window.location.search,
  browser: navigator.userAgent,
  os: navigator.userAgentData?.platform || navigator.platform || '',
  screenResolution: `${window.screen.width}x${window.screen.height}`,
  appVersion: process.env.REACT_APP_VERSION || '',
});
